import * as Sentry from "@sentry/nextjs";

export interface MissingExternalAccountIdWarning {
  /**
   * Human-readable description of where the identity gap was observed, e.g.
   * `Stripe webhook checkout.session.completed could not resolve externalAccountId`.
   */
  message: string;
  /** Sentry tag for the subsystem (defaults to `statsig`). */
  component?: string;
  /** Sentry tag for the operation (defaults to `identify`). */
  action?: string;
  /** Extra context attached to the Sentry event. */
  extra?: Record<string, unknown>;
}

/**
 * Single source of truth for the "externalAccountId is absent" warning.
 *
 * externalAccountId is the sole unit of cohorting (see `TAXONOMY.md`); when it's
 * missing, the caller can't bucket an experiment or attribute an event, so we
 * surface it. Centralizing the level/tag schema here keeps the warnings
 * groupable and makes future changes (rate-limiting, sampling, severity) a
 * one-line edit instead of touching every call site.
 *
 * Fire this **once per request/operation** — never per experiment eval — to
 * avoid flooding Sentry on high-volume pre-auth traffic.
 */
export function warnMissingExternalAccountId({
  message,
  component = "statsig",
  action = "identify",
  extra,
}: MissingExternalAccountIdWarning): void {
  Sentry.captureMessage(message, {
    level: "warning",
    tags: { component, action },
    ...(extra ? { extra } : {}),
  });
}
